<template>
  <div v-if="hasAnyPermission('abbreviation-view')" class="py-4 container-fluid">
    <ToastPrime position="bottom-left" />
    <div class="card">
      <div class="card-header py-3">
        <div class="row align-items-center">
          <div class="col-4">
            <h5 class="mb-0">Abbreviation</h5>
          </div>
          <div class="col-4 d-flex justify-content-center">
            <IconField class="w-100">
              <InputIcon>
                <i class="pi pi-search"></i>
              </InputIcon>
              <InputText
v-model="table.search" type="text" size="small" placeholder="Search" class="rounded-pill w-100"
                @keyup="filter" />
            </IconField>
          </div>
          <div v-if="hasAnyPermission('abbreviation-create')" class="col-4 text-end">
            <button type="button" class="mx-1 mb-0 btn btn-sm btn-primary" @click="openAddModal">
              Add
            </button>
          </div>
        </div>
      </div>

      <div style="height: 500px; overflow-y: auto;">
        <DataTable
:value="table.onLoad ? dummyRows : table.data" class="datatable-sm text-sm" removable-sort scrollable scroll-height="flex"
          table-style="min-width: 50rem">
          <Column field="code" header="Code" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data.code || '' }}
            </template>
          </Column>

          <Column field="description" header="Description" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data.description || '' }}
            </template>
          </Column>

          <!-- <Column field="#" header="Actions" headerClass="bg-light text-dark text-end"  bodyClass="text-start"   style="width: 8rem;">-->
          <Column
header="Actions" header-class="bg-light" :style="{ width: '150px' }"
            :body-style="{ width: '150px', padding: '0' }">
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              <ButtonPrime v-if="hasAnyPermission('abbreviation-update')"
v-tooltip.bottom="'Update'" icon="pi pi-pencil" outlined rounded size="small" class="me-2"
                @click="openEditModal(slotProps.data)" />
              <ButtonPrime v-if="hasAnyPermission('abbreviation-delete')"
v-tooltip.bottom="'Delete'" icon="pi pi-trash" outlined rounded severity="danger"
                size="small" @click="deleteItem(slotProps.data.id)" />
            </template>
          </Column>

          <template #empty>
            <div style="text-align: center; padding: 40px 0;">
              <img src="/no_data.png" alt="No Data" style="max-width: 330px; opacity: 0.7; margin-bottom: 0;" />
              <div style="color: #888; font-size: 1.1em; font-weight: 500;">
                No Data
              </div>
            </div>
          </template>
        </DataTable>
      </div>

      <div class="card-footer pb-0 d-flex justify-content-between align-items-center">
        <BasePagination
v-model="table.page.current_pages" :page-count="table.page.total_pages || 1" size="sm"
          @click="changePage()" />
        <p>
          {{
            (table.page.current_pages - 1) * table.page.per_pages +
            table.data.length
          }}
          of {{ table.total }}
        </p>
      </div>
    </div>

    <!-- Modal -->
    <DialogPrime
v-model:visible="modal.visible" modal :header="modal.title" style="width: 30rem; margin: 10px;"
      class="client-modal">
      <template #header>
        <div class="d-flex align-items-center gap-3">
          <div>
            <h5 class="mb-0 fw-bold text-dark">{{ modal.title }}</h5>
          </div>
        </div>
      </template>
      <div class="row g-4">
        <div class="col-12">
          <div class="row g-3">
            <div class="mb-2">
              <label for="code" class="font-semibold">Code <span class="text-danger">*</span></label>
              <InputText
id="code" v-model="modal.data.code" placeholder="Code" :class="{
                'border-danger': modal.errorMessage['code'] && !modal.data.code
              }" fluid />
              <small v-if="modal.errorMessage['code'] && !modal.data.code" class="text-danger">{{
                modal.errorMessage['code']
                }}</small>
            </div>
            <div class="">
              <label for="description" class="font-semibold">Description <span class="text-danger">*</span></label>
              <InputText
id="description" v-model="modal.data.description" placeholder="Description" :class="{
                'border-danger':
                  modal.errorMessage['description'] && !modal.data.description
              }" fluid />
              <small v-if="modal.errorMessage['description'] && !modal.data.description" class="text-danger">{{
                modal.errorMessage['description'] }}</small>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
          <ButtonPrime
type="button" label="Cancel" severity="danger" raised size="small" class="px-4 border-0"
            @click="closeModal()" />
          <ButtonPrime
type="button" severity="contrast" raised size="small" :disabled="modal.onLoading" label="Save Abbreviation"
            class="px-4" @click="save()">
            <template v-if="modal.onLoading" #icon>
              <i class="fas fa-spinner fa-spin me-2"></i>
            </template>
          </ButtonPrime>
        </div>
      </template>
    </DialogPrime>
  </div>
  <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import NoAccess from "@/components/NoAccess.vue";
import BasePagination from "@/components/BasePagination.vue";
import api from "@/helpers/api";
import abbreviationService from "@/services/dictionary/abbreviationService";

export default {
  components: {
    BasePagination,
    NoAccess,
  },
  computed: {
    ...mapState({
      authUserPermissions: (state) => state.auth.userHasPermissions || [],
    }),
  },
  data() {
    return {
      table: {
        onLoad: true,
        search: this.$route.query.search || "",
        total: 0,
        data: [],
        limit: 100,
        page: {
          current_pages: 1,
          per_pages: 10,
          total_pages: 1,
        },
        loadTimeout: null,
      },
      dummyRows: Array.from({ length: 10 }, () => ({})),
      modal: {
        visible: false,
        data: { code: "", description: "" },
        errorMessage: {},
        onLoading: false,
        action: "",
        editId: null,
        title: "",
      },
    };
  },
  mounted() {
    if (this.hasAnyPermission('abbreviation-view')) {
      this.get();
    }
  },
  methods: {
    hasAnyPermission(...perms) {
      return perms.some((p) => this.authUserPermissions.includes(p));
    },
    async get() {
      this.table.onLoad = true;
      try {
        const response = await api(
          this,
          abbreviationService.list({
            search: this.table.search,
            limit: this.table.limit,
            page: this.table.page.current_pages,
          }),
          false
        );
        const paged = response.data || {};
        this.table.data = paged.data || [];
        this.table.total = paged.total || this.table.data.length;
        this.table.page.total_pages = paged.last_page || 1;
        this.table.page.current_pages = paged.current_page || 1;
        this.table.page.per_pages = paged.per_page || this.table.limit;
      } catch {
        this.table.data = [];
        this.table.total = 0;
        this.table.page.total_pages = 1;
      } finally {
        this.table.onLoad = false;
      }
    },
    filter() {
      clearTimeout(this.table.loadTimeout);
      this.table.loadTimeout = setTimeout(() => {
        this.$router.push({ query: this.table.search ? { search: this.table.search } : null });
        this.table.page.current_pages = 1;
        this.get();
      }, 500);
    },
    changePage() {
      this.get();
    },

    openAddModal() {
      this.modal.visible = true;
      this.modal.data = { code: "", description: "" };
      this.modal.errorMessage = {};
      this.modal.editId = null;
      this.modal.title = "Add Abbreviation";
      this.modal.action = "create";
    },

    // 🟡 Tanpa API detail
    openEditModal(rowData) {
      this.modal.visible = true;
      this.modal.action = "update";
      this.modal.editId = rowData.id;
      this.modal.data = {
        code: rowData.code || "",
        description: rowData.description || "",
      };
      this.modal.errorMessage = {};
      this.modal.title = "Edit Abbreviation";
      this.modal.onLoading = false;
    },

    async save() {
      this.modal.errorMessage = {};
      if (!this.modal.data.code)
        this.modal.errorMessage["code"] = "Abbreviation required";
      if (!this.modal.data.description)
        this.modal.errorMessage["description"] = "Description required";
      if (Object.keys(this.modal.errorMessage).length > 0) return;

      this.modal.onLoading = true;
      try {
        if (this.modal.action === "update" && this.modal.editId) {
          await api(
            this,
            abbreviationService.patch(this.modal.editId, this.modal.data),
            true
          );
        } else {
          await api(this, abbreviationService.create(this.modal.data), true);
        }
        this.modal.visible = false;
        this.get();
      } catch (error) {
        this.handleErrorObjectNested(error);
      } finally {
        this.modal.onLoading = false;
        this.modal.editId = null;
        this.modal.action = "";
      }
    },

    closeModal() {
      this.modal.visible = false;
      this.modal.data = { code: "", description: "" };
      this.modal.errorMessage = {};
      this.modal.editId = null;
      this.modal.action = "";
    },

    async deleteItem(id) {
      this.confirmDialog("You won't be able to revert this!", "delete it").then(
        (result) => {
          if (result.isConfirmed) {
            api(this, abbreviationService.delete(id)).then(() => {
              this.get();
            });
          }
        }
      );
    },

    handleErrorObjectNested(error) {
      if (error.response?.data?.code == 400 && error.response.data.data) {
        error.response.data.data.forEach((v) => {
          this.modal.errorMessage[v.path] = v.msg;
        });
      } else if (
        error.response?.data?.code == 400 ||
        error.response?.status == 422 ||
        error.response?.status == 500
      ) {
        const errorData = error.response?.data?.data;
        if (typeof errorData === "object" && errorData !== null) {
          for (const key in errorData) {
            if (Object.prototype.hasOwnProperty.call(errorData, key)) {
              const match = key.match(/^items\.(\d+)\.(.+)$/);
              if (match) {
                error.response.data.data[key].forEach((message) => {
                  this.$toastNotifPrime(
                    "error",
                    error.response.data.message,
                    `${message} in row ${parseInt(match[1]) + 1}`
                  );
                });
              } else {
                this.$toastNotifPrime(
                  "error",
                  error.response.data.message,
                  errorData[key]
                );
              }
            }
          }
        }
      }
    },
  },
};
</script>
